import React from "react";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1E] text-white p-6 font-sans selection:bg-[#00FFAB] selection:text-[#0A0F1E]">
      <div className="fixed top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#00FFAB] opacity-10 blur-[120px] pointer-events-none"></div>

      <div className="max-w-3xl mx-auto relative z-10">
        {/* HEADER */}
        <header className="mb-12 border-b border-[#00FFAB]/20 pb-8">
          <h1 className="text-4xl font-bold text-[#00FFAB] mb-2 tracking-tight">
            Privacy Policy for Minty AI
          </h1>
          <p className="text-gray-400 font-mono">
            Effectivity Date: May 05, 2026
          </p>
        </header>

        {/* CONTENT */}
        <main className="space-y-10 leading-relaxed text-gray-300">
          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              1. Introduction
            </h2>
            <p>
              Minty AI is a read-only application that provides curated AI
              prompt presets. We are committed to protecting your privacy. This
              Privacy Policy explains how information is handled when you use
              our application.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              2. Information We Do Not Collect
            </h2>

            <p className="mb-3">
              This application does not require registration and does not
              collect personal information such as:
            </p>

            <ul className="pl-6 space-y-2">
              <li className="pl-2">• Name</li>
              <li className="pl-2">• Email address</li>
              <li className="pl-2">• Phone number</li>
              <li className="pl-2">• Any personal identity data</li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              3. Backend Service (Appwrite)
            </h2>

            <p className="mb-3">
              We use Appwrite Cloud to retrieve and deliver app content.
            </p>

            <p className="mb-3">
              When the app communicates with Appwrite, the following technical
              data may be processed automatically:
            </p>

            <ul className="pl-6 space-y-2">
              <li className="pl-2">
                • IP address (for security and request handling)
              </li>
              <li className="pl-2">• Device type and system information</li>
              <li className="pl-2">• Request timestamps</li>
            </ul>

            <p className="mt-3">
              This data is used only for app functionality, security, and
              performance and is not used to identify users.
            </p>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4 uppercase tracking-wider">
              4. Third-Party AI Services & Usage
            </h2>

            <div className="space-y-4">
              <p>
                <strong>Minty AI</strong> provides manually crafted prompt
                presets that users may copy and use with third-party AI tools of
                their choice, such as{" "}
                <strong>ChatGPT, Google Gemini and Grok</strong>.
              </p>

              <p>
                Users are solely responsible for how they use these prompts in
                external services. Minty AI does not provide, operate, or
                control any third-party AI tools and does not process or store
                AI-generated responses. Your interactions with these external
                platforms are governed by their respective privacy policies and
                terms of service.
              </p>
            </div>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              5. Data Storage
            </h2>
            <p>
              All backend data is hosted and processed through Appwrite Cloud in
              accordance with their privacy policy.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              6. Data Sharing
            </h2>
            <p>
              We do not sell, trade, or share personal information. Any
              technical data processed is only used for app operation and
              performance.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              7. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will
              be posted on this page with an updated effective date.
            </p>
          </section>

          {/* 8 - ADDED */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              8. Data Deletion Requests
            </h2>

            <p className="mb-3">
              Minty AI does not require user accounts and does not store
              personal user profiles or identity data.
            </p>

            <p className="mb-3">
              However, limited technical data (such as IP address, device
              information, and request logs) may be processed temporarily by our
              backend service (Appwrite Cloud) for security and performance
              purposes.
            </p>

            <p className="mb-3">
              If you have concerns or wish to request deletion of any associated
              technical data, you may contact us at:
            </p>

            <p className="text-[#00FFAB] mb-3">upreyvan@gmail.com</p>

            <p>
              Most technical logs are automatically managed and periodically
              deleted by Appwrite Cloud in accordance with their retention and
              security policies.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              9. Advertising (Google AdMob)
            </h2>
            <p className="mb-3">
              We use <strong>Google AdMob</strong> to display advertisements in
              Minty AI. AdMob may collect and process information about your
              device and how you interact with ads to provide personalized or
              relevant advertising.
            </p>
            <p className="mb-3">The data collected by AdMob may include:</p>
            <ul className="pl-6 space-y-2 mb-3">
              <li className="pl-2">• Android Advertising ID (GAID)</li>
              <li className="pl-2">• IP address and device information</li>
              <li className="pl-2">
                • Interactions with advertisements (clicks, views)
              </li>
            </ul>
            <p>
              These activities are governed by Google's Privacy Policy. You can
              learn more here:
              <a
                href="https://policies.google.com/privacy"
                className="text-[#00FFAB] ml-1"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy & Terms
              </a>
            </p>
          </section>

          {/* 10 */}
          <section className="pb-10">
            <h2 className="text-xl font-semibold text-white mb-3">
              10. Contact Us
            </h2>
            <p>If you have any questions, contact us at:</p>
            <p className="text-[#00FFAB] mt-2">upreyvan@gmail.com</p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Privacy;
